import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import {
	Send,
	Phone,
	Video,
	MoreVertical,
	Paperclip,
	Shield,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/lib/supabase";
import ContactInfoWarningModal from "@/components/ui/ContactInfoWarningModal";
import MessageFilteredModal from "@/components/ui/MessageFilteredModal";

interface Message {
	id: string;
	content: string;
	sender_user_id: string;
	sender_name: string;
	created_at: string;
	is_read: boolean;
}

interface MessageThreadProps {
	conversationId: string;
	participantName: string;
	participantAvatar: string;
	userType: "parent" | "doula" | "admin";
	onStartVideoCall?: () => void;
}

const MessageThread: React.FC<MessageThreadProps> = ({
	conversationId,
	participantName,
	participantAvatar,
	userType,
	onStartVideoCall,
}) => {
	const [messages, setMessages] = useState<Message[]>([]);
	const [newMessage, setNewMessage] = useState("");
	const [loading, setLoading] = useState(false);
	const [isOnline, setIsOnline] = useState(true);
	const [userProfile, setUserProfile] = useState<{ id: string } | null>(null);
	const messagesEndRef = useRef<HTMLDivElement>(null);

	// Modal states
	const [showWarningModal, setShowWarningModal] = useState(false);
	const [showFilteredModal, setShowFilteredModal] = useState(false);
	const [pendingMessage, setPendingMessage] = useState("");
	const [detectedIssues, setDetectedIssues] = useState<string[]>([]);

	useEffect(() => {
		loadUserProfile();
		loadMessages();
		markMessagesAsRead();

		// Set up real-time subscription
		const subscription = supabase
			.channel(`messages:${conversationId}`)
			.on(
				"postgres_changes",
				{
					event: "INSERT",
					schema: "public",
					table: "messages",
					filter: `conversation_id=eq.${conversationId}`,
				},
				async (payload) => {
					const newMsg = payload.new as any;

					// Fetch sender information for the new message
					const { data: senderData } = await supabase
						.from("users")
						.select("full_name")
						.eq("id", newMsg.sender_user_id)
						.single();

					const messageWithSender = {
						...newMsg,
						sender_name: senderData?.full_name || "Unknown User",
					};

					setMessages((prev) => [...prev, messageWithSender]);
					scrollToBottom();
				},
			)
			.subscribe();

		return () => {
			subscription.unsubscribe();
		};
	}, [conversationId]);

	const loadUserProfile = async () => {
		try {
			const {
				data: { user },
			} = await supabase.auth.getUser();
			if (!user) return;

			const { data: profile } = await supabase
				.from("users")
				.select("id")
				.eq("auth_user_id", user.id)
				.single();

			setUserProfile(profile);
		} catch (error) {
			console.error("Error loading user profile:", error);
		}
	};

	useEffect(() => {
		scrollToBottom();
	}, [messages]);

	const loadMessages = async () => {
		try {
			const { data, error } = await supabase
				.from("messages")
				.select(
					`
          *,
          sender:users!messages_sender_user_id_fkey(
            id,
            full_name
          )
        `,
				)
				.eq("conversation_id", conversationId)
				.order("created_at", { ascending: true });

			if (error) throw error;

			// Transform the data to include sender name
			const transformedMessages = (data || []).map((message) => ({
				...message,
				sender_name: message.sender?.full_name || "Unknown User",
			}));

			setMessages(transformedMessages);
		} catch (error) {
			console.error("Error loading messages:", error);
		}
	};

	const markMessagesAsRead = async () => {
		try {
			const {
				data: { user },
			} = await supabase.auth.getUser();
			if (!user) return;

			// Get user profile
			const { data: userProfile } = await supabase
				.from("users")
				.select("id")
				.eq("auth_user_id", user.id)
				.single();

			if (!userProfile) return;

			await supabase
				.from("messages")
				.update({ is_read: true })
				.eq("conversation_id", conversationId)
				.neq("sender_user_id", userProfile.id)
				.eq("is_read", false);
		} catch (error) {
			console.error("Error marking messages as read:", error);
		}
	};

	// Function to detect contact information in message content
	const detectContactInfo = (content: string) => {
		const issues = [];

		// Check for phone numbers
		const phoneRegex =
			/(\+?1[-.\s]?)?\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})/g;
		if (phoneRegex.test(content)) {
			issues.push("phone numbers");
		}

		// Check for email addresses
		const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
		if (emailRegex.test(content)) {
			issues.push("email addresses");
		}

		// Check for URLs
		const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/g;
		if (urlRegex.test(content)) {
			issues.push("website links");
		}

		// Check for common contact-sharing phrases
		const contactPhrases = [
			/call me at/i,
			/text me at/i,
			/my number is/i,
			/reach me at/i,
			/contact me at/i,
			/email me at/i,
		];

		for (const phrase of contactPhrases) {
			if (phrase.test(content)) {
				issues.push("contact information");
				break;
			}
		}

		return issues;
	};

	const sendMessage = async () => {
		if (!newMessage.trim()) return;

		// Check for contact information and show warning modal
		const contactIssues = detectContactInfo(newMessage.trim());
		if (contactIssues.length > 0) {
			setPendingMessage(newMessage.trim());
			setDetectedIssues(contactIssues);
			setShowWarningModal(true);
			return; // Wait for user confirmation via modal
		}

		// If no issues, send directly
		await sendMessageToAPI(newMessage.trim());
	};

	const sendMessageToAPI = async (messageContent: string) => {
		setLoading(true);
		try {
			const {
				data: { user },
			} = await supabase.auth.getUser();
			if (!user) throw new Error("User not authenticated");

			// Get user profile for sender ID
			const { data: profile } = await supabase
				.from("users")
				.select("id")
				.eq("auth_user_id", user.id)
				.single();

			if (!profile) throw new Error("User profile not found");

			// Use edge function to send message
			const response = await fetch(
				`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-message`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
					},
					body: JSON.stringify({
						conversationId: conversationId,
						content: messageContent,
						senderUserId: profile.id,
					}),
				},
			);

			const result = await response.json();

			if (!response.ok) {
				console.error("Error sending message:", result.error);
				alert("Failed to send message. Please try again.");
				return;
			}

			// Show success modal if content was filtered
			if (result.contentFiltered) {
				setShowFilteredModal(true);
			}

			setNewMessage("");
			setMessages((prev) => [...prev, result.data]);
			scrollToBottom();
		} catch (error) {
			console.error("Error sending message:", error);
			alert("Failed to send message. Please try again.");
		} finally {
			setLoading(false);
		}
	};

	const handleConfirmSend = async () => {
		setShowWarningModal(false);
		await sendMessageToAPI(pendingMessage);
		setPendingMessage("");
		setDetectedIssues([]);
	};

	const handleCancelSend = () => {
		setShowWarningModal(false);
		setPendingMessage("");
		setDetectedIssues([]);
	};

	const scrollToBottom = () => {
		messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
	};

	const formatTime = (dateString: string) => {
		const date = new Date(dateString);
		return date.toLocaleTimeString("en-US", {
			hour: "numeric",
			minute: "2-digit",
			hour12: true,
		});
	};

	const formatDate = (dateString: string) => {
		const date = new Date(dateString);
		const today = new Date();
		const yesterday = new Date(today);
		yesterday.setDate(yesterday.getDate() - 1);

		if (date.toDateString() === today.toDateString()) {
			return "Today";
		} else if (date.toDateString() === yesterday.toDateString()) {
			return "Yesterday";
		} else {
			return date.toLocaleDateString("en-US", {
				weekday: "long",
				year: "numeric",
				month: "long",
				day: "numeric",
			});
		}
	};

	const groupMessagesByDate = () => {
		const groups: { [key: string]: Message[] } = {};

		messages.forEach((message) => {
			const date = new Date(message.created_at).toDateString();
			if (!groups[date]) {
				groups[date] = [];
			}
			groups[date].push(message);
		});

		return groups;
	};

	const messageGroups = groupMessagesByDate();

	return (
		<div className="flex-1 flex flex-col h-full">
			{/* Header */}
			<div className="p-4 border-b bg-white flex items-center justify-between">
				<div className="flex items-center space-x-3">
					<div className="relative">
						<img
							src={participantAvatar}
							alt={participantName}
							className="w-10 h-10 rounded-full object-cover"
						/>
					</div>
					<div>
						<h3 className="font-medium">{participantName}</h3>
					</div>
				</div>
			</div>

			{/* Messages */}
			<div className="flex-1 overflow-y-auto p-4 space-y-4">
				{Object.entries(messageGroups).map(([date, dateMessages]) => (
					<div key={date}>
						{/* Date separator */}
						<div className="flex items-center justify-center my-4">
							<div className="bg-gray-100 text-gray-600 text-xs px-3 py-1 rounded-full">
								{formatDate(dateMessages[0].created_at)}
							</div>
						</div>

						{/* Messages for this date */}
						{dateMessages.map((message, index) => {
							const isFromCurrentUser =
								message.sender_user_id === userProfile?.id;

							return (
								<motion.div
									key={message.id}
									initial={{ opacity: 0, y: 10 }}
									animate={{ opacity: 1, y: 0 }}
									transition={{ delay: index * 0.05 }}
									className={`flex ${isFromCurrentUser ? "justify-end" : "justify-start"} mb-3`}
								>
									<div
										className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
											isFromCurrentUser
												? "bg-primary-600 text-white"
												: "bg-gray-200 text-gray-900"
										}`}
									>
										<p className="text-sm">{message.content}</p>
										<div className="flex items-center justify-between mt-1">
											<p
												className={`text-xs ${
													isFromCurrentUser
														? "text-primary-200"
														: "text-gray-500"
												}`}
											>
												{formatTime(message.created_at)}
											</p>
											{isFromCurrentUser && message.read_at && (
												<div className="text-xs text-primary-200">✓✓</div>
											)}
										</div>
									</div>
								</motion.div>
							);
						})}
					</div>
				))}
				<div ref={messagesEndRef} />
			</div>

			{/* Message Input */}
			<div className="p-4 border-t bg-white">
				<div className="flex items-center space-x-2">
					<Input
						value={newMessage}
						onChange={(e) => setNewMessage(e.target.value)}
						placeholder="Type your message..."
						className="flex-1 shadow-sm"
						onKeyPress={(e) =>
							e.key === "Enter" && !e.shiftKey && sendMessage()
						}
					/>
					<Button
						onClick={sendMessage}
						disabled={!newMessage.trim() || loading}
						className="bg-primary-600 hover:bg-primary-700 shadow-sm"
					>
						<Send className="h-4 w-4" />
					</Button>
				</div>
			</div>

			{/* Contact Info Warning Modal */}
			<ContactInfoWarningModal
				isOpen={showWarningModal}
				onClose={handleCancelSend}
				onContinue={handleConfirmSend}
				contactIssues={detectedIssues}
				userType={userType}
			/>

			{/* Message Filtered Success Modal */}
			<MessageFilteredModal
				isOpen={showFilteredModal}
				onClose={() => setShowFilteredModal(false)}
				userType={userType}
			/>
		</div>
	);
};

export default MessageThread;
